import {useContext, useEffect, useRef, useState} from 'react';
import {GameContext} from '../../../contexts/GameContext';
import {Box, Stack} from '@mui/material';
import GameHeader from '../GameFooter_Header/GameHeader';
import GameSettings from '../../../Settings/GameSettings';
import SocketContext from '../../../contexts/SocketContext';
import {UpdateCursorPositionRequest, UpdateCursorPositionResponse} from '../../../socket_messages/UpdateCursor';
import GameFooter from '../GameFooter_Header/GameFooter';
import GameBoard from '../GameBoard/GameBoard';
import WaitingLobby from '../WaitingLobby/WaitingLobby';
import VictoryModal from '../VictoryModal/VictoryModal';
import NoWinnerOverlay from '../NoWinnerOverlay/NoWinnerOverlay';
import {AlertContext} from "../../../contexts/AlertContext";

const gameSettings = new GameSettings();

type GameLobbyComponentsProps = {
    gameId: string;
};

const GameLobbyComponents = (props: GameLobbyComponentsProps) => {
    const {socket} = useContext(SocketContext).SocketState;
    const gameContext = useContext(GameContext);
    const alertContext = useContext(AlertContext);
    const [cursors, setCursors] = useState<{ [id: string]: { x: number; y: number; cursorIcon: string } }>({});
    const [cursorImage, setCursorImage] = useState<string>(gameSettings.cursorImage);

    const dateTimeRef = useRef(new Date());

    useEffect(() => {
        const handleMouseMove = async (event: MouseEvent) => {
            const currentTime = new Date();
            const timeElapsed = currentTime.getTime() - dateTimeRef.current.getTime();

            if (timeElapsed <= 20) {
                // If less than or equal to 50ms have passed, do nothing
                return;
            }

            const width = window.innerWidth;
            const height = window.innerHeight;

            // Update dateTime to the current time
            dateTimeRef.current = currentTime;

            const cursorData = {
                x: (event.clientX / width) * 100,
                y: (event.clientY / height) * 100
            };

            // Envoyer la position du curseur au serveur
            socket?.emit(UpdateCursorPositionRequest.Message, cursorData.x, cursorData.y);
        };

        // Écouter les mouvements de la souris
        window.addEventListener('mousemove', handleMouseMove);

        socket?.on(UpdateCursorPositionResponse.Message, (args: UpdateCursorPositionResponse) => {
            const height = window.innerHeight;

            const margin = (5 / 100) * height;

            const getY = (cursorY: number): number => {
                const maxHeight = (cursorY / 100) * window.innerHeight;

                return Math.min(Math.max(maxHeight, 0), window.innerHeight - margin);
            };

            const getX = (cursorX: number): number => {
                const maxWidth = (cursorX / 100) * window.innerWidth;

                return Math.min(Math.max(maxWidth, 0), window.innerWidth - margin);
            };

            const cursorX = getX(args.cursorX);
            const cursorY = getY(args.cursorY);

            setCursors((prevCursors) => ({
                ...prevCursors,
                [args.socketId]: {
                    x: cursorX,
                    y: cursorY,
                    cursorIcon: args.logo
                }
            }));
        });

        // Cleanup the event listener on component unmount
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            socket?.off(UpdateCursorPositionResponse.Message);
        };
    }, [props.gameId]);

    useEffect(() => {
        const previousBodyOverflow = document.body.style.overflow;
        const previousHtmlOverflow = document.documentElement.style.overflow;
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = previousBodyOverflow;
            document.documentElement.style.overflow = previousHtmlOverflow;
        };
    }, []);

    // Nettoyer les curseurs des joueurs déconnectés
    useEffect(() => {
        const playerIds = new Set(gameContext.players.map((p) => p.playerId));
        setCursors((prev) => {
            const next: typeof prev = {};
            for (const id of Object.keys(prev)) {
                if (playerIds.has(id)) {
                    next[id] = prev[id];
                }
            }
            return next;
        });
    }, [gameContext.players]);

    useEffect(() => {
        gameContext.getClient()
            .SynchronizeGameValues()
            .then((result) => {
                if (!result.success) {
                    alertContext?.setAlertMessage(result.errorMessage);
                    return;
                }

                gameContext.setPlayers(result.value.players);

                if (result.value.selectedPlaylist) {
                    gameContext.setSelectedPlaylist(result.value.selectedPlaylist);
                }

                if (result.value.cardsToWin) {
                    gameContext.setCardsToWin(result.value.cardsToWin);
                }

                // Si un tour existe déjà, la partie a déjà commencé (reconnexion)
                if (result.value.currentTurn) {
                    gameContext.setCurrentTurn(result.value.currentTurn);
                    gameContext.setGameStarted(true);
                }

                // Le créateur est le premier joueur de la liste
                if (result.value.players.length > 0 && result.value.players[0].playerId === socket?.id) {
                    gameContext.setIsCreator(true)
                }
            });
    }, [props.gameId]);

    return (
        <>
            <WaitingLobby/>
            <VictoryModal/>
            <NoWinnerOverlay/>
            <Box
                sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    '@supports (height: 100dvh)': { height: '100dvh' },
                    overflow: 'hidden',
                    pointerEvents: 'none',
                    zIndex: 100,
                }}
            >
                {Object.keys(cursors).map((id) => (
                    <div
                        key={id}
                        style={{
                            position: 'absolute',
                            left: `${cursors[id].x}px`,
                            top: `${cursors[id].y}px`,
                            pointerEvents: 'none',
                        }}
                    >
                        <Box
                            sx={{
                                backgroundImage: cursors[id].cursorIcon,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                backgroundRepeat: 'no-repeat',
                                height: '32px',
                                width: '32px'
                            }}
                        />
                    </div>
                ))}
            </Box>
            {gameContext.gameStarted ?
                <Box
                    display="flex"
                    sx={{
                        backgroundColor: '#101728',
                        height: '100vh',
                        '@supports (height: 100dvh)': { height: '100dvh' },
                        width: '100vw',
                        overflow: 'hidden',
                        paddingBottom: { xs: '140px', sm: 0 },
                        boxSizing: 'border-box',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        cursor: `${cursorImage}, auto`
                    }}
                >
                    <Stack
                        spacing={{ xs: 1, sm: 10 }}
                        sx={{
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                            height: '100%',
                            width: '100%',
                            display: 'flex',
                            '& > .game-board-fill': { flex: 1, minHeight: 0 },
                        }}
                    >
                        <GameHeader staticCursorImage={cursorImage!}/>
                        <Box className="game-board-fill" sx={{ width: '100%', display: 'flex' }}>
                            <GameBoard/>
                        </Box>
                        <GameFooter/>
                    </Stack>
                </Box>
                : null}
        </>
    );
};

export default GameLobbyComponents;
